import { useEffect, useRef } from "react";

const LocationLogsWebhook = ({ userInfoData, onLocationUpdate }) => {
  const wsRef = useRef(null);
  const wsBackoffRef = useRef(2000);

  const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent);

  useEffect(() => {
    const WS_URL = process.env.REACT_APP_ODM_WSS_URL;

    if (!WS_URL || !userInfoData || isIOS()) {
      console.log("❌ LocationLogsWebhook: Skipping connection", {
        hasUrl: !!WS_URL,
        hasUser: !!userInfoData,
        isIOS: isIOS(),
      });
      return;
    }

    let stopped = false;

    const computeChannels = () => {
      try {
        const acc = userInfoData?.account_type;
        const channels = [];

        if (acc === "receptionist" && userInfoData?.gym_id) {
          channels.push(`tracking:gym:${userInfoData.gym_id}`);
        }
        if (acc === "cluster_manager") {
          const userGyms = userInfoData?.gyms?.gyms || userInfoData?.gyms || [];
          if (Array.isArray(userGyms)) {
            userGyms.forEach((g) => {
              const gid = g?.value || g?.gym_id || g?.uid || g;
              if (gid) channels.push(`tracking:gym:${gid}`);
            });
          }
        }
        if (acc === "admin") {
          channels.push("tracking:global");
        }

        return Array.from(new Set(channels));
      } catch {
        return [];
      }
    };

    const connect = () => {
      if (stopped) return;
      try {
        const wsUrl = `${WS_URL}`;
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        console.log("🔌 [LocationLogs WebSocket] Connecting to:", wsUrl);

        ws.onopen = () => {
          console.log("✅ [LocationLogs WebSocket] Connected");
          wsBackoffRef.current = 2000;
          const channels = computeChannels();

          console.log(
            "📡 [LocationLogs WebSocket] Subscribing to channels:",
            channels
          );
          try {
            ws.send(
              JSON.stringify({
                type: "SUBSCRIBE",
                channels: channels,
                event_type: ["location_logs_update"],
              })
            );
            console.log(
              "✅ [LocationLogs WebSocket] Subscription message sent"
            );
          } catch (e) {
            console.error("❌ [LocationLogs WebSocket] Subscription error:", e);
          }
        };

        ws.onmessage = (e) => {
          try {
            const msg = JSON.parse(e.data);
            console.log("📨 [LocationLogs WebSocket] Message received:", msg);

            // Check if this is a location logs update
            if (
              msg?.link_type === "locationLogsScreen" ||
              msg?.status === "location_logs_update"
            ) {
              console.log("------locationLogsObj---", msg);

              // Call the callback to update the location logs
              if (onLocationUpdate && msg?.data) {
                onLocationUpdate(msg.data);
              }
            }
          } catch (err) {
            console.error("❌ [LocationLogs WebSocket] Parse error:", err);
          }
        };

        ws.onclose = () => {
          console.log("🔴 [LocationLogs WebSocket] Connection closed");
          if (stopped) return;
          const delay = Math.min(wsBackoffRef.current, 30000);
          console.log(
            `🔄 [LocationLogs WebSocket] Reconnecting in ${delay}ms...`
          );
          setTimeout(connect, delay);
          wsBackoffRef.current = Math.min(wsBackoffRef.current * 2, 30000);
        };

        ws.onerror = (error) => {
          console.error("❌ [LocationLogs WebSocket] Error:", error);
          try {
            ws.close();
          } catch {}
        };
      } catch (error) {
        console.error("❌ [LocationLogs WebSocket] Connection error:", error);
        const delay = Math.min(wsBackoffRef.current, 30000);
        setTimeout(connect, delay);
        wsBackoffRef.current = Math.min(wsBackoffRef.current * 2, 30000);
      }
    };

    connect();

    return () => {
      console.log("🛑 [LocationLogs WebSocket] Cleanup");
      stopped = true;
      try {
        if (wsRef.current && wsRef.current.readyState === 1) {
          wsRef.current.close();
        }
      } catch {}
    };
  }, [userInfoData, onLocationUpdate]);

  return null;
};

export default LocationLogsWebhook;
